/**
 * Primitivas UI ligeras con Tailwind. Cuando crezca, migramos a shadcn/ui.
 */
import { forwardRef, useEffect, type ButtonHTMLAttributes, type InputHTMLAttributes, type LabelHTMLAttributes, type ReactNode } from "react";
import { X } from "lucide-react";

// ---------- Button ----------
type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "success";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
}

// El color de un botón dice QUÉ HACE, no de quién es el software. Por eso
// ninguno usa el color de marca: ver index.css.
const buttonStyles: Record<ButtonVariant, string> = {
  // La acción principal de la pantalla. Oscura y neutra: destaca sobre
  // cualquier fondo y no compite con rojo ni verde.
  primary:
    "bg-[color:var(--color-action)] hover:brightness-125 text-white shadow-sm focus:ring-slate-500",
  // Alternativa de igual peso pero no la esperada (p.ej. "Guardar borrador").
  secondary:
    "bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 shadow-sm focus:ring-slate-400",
  // Terciaria: vive dentro de una fila o una tarjeta y no debe pesar.
  ghost:
    "bg-transparent hover:bg-slate-100 text-slate-700 focus:ring-slate-400",
  // Destructiva o irreversible. Rojo, siempre.
  danger:
    "bg-[color:var(--color-danger)] hover:brightness-110 text-white shadow-sm focus:ring-red-500",
  // Confirmación de algo que entra: cobrar, validar. Verde, siempre.
  success:
    "bg-[color:var(--color-success)] hover:brightness-110 text-white shadow-sm focus:ring-emerald-500",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", loading, disabled, className = "", children, ...rest }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${buttonStyles[variant]} ${className}`}
      {...rest}
    >
      {loading && (
        <span className="inline-block w-4 h-4 border-2 border-current border-r-transparent rounded-full animate-spin" />
      )}
      {children}
    </button>
  ),
);
Button.displayName = "Button";

// ---------- Input ----------
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error, className = "", ...rest }, ref) => (
    <input
      ref={ref}
      className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-0 ${error ? "border-red-300 focus:ring-red-400" : "border-slate-300 focus:ring-slate-400"} ${className}`}
      {...rest}
    />
  ),
);
Input.displayName = "Input";

// ---------- Label ----------
export function Label({ className = "", ...rest }: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={`block text-sm font-medium text-slate-700 mb-1 ${className}`}
      {...rest}
    />
  );
}

// ---------- Field (Label + Input + error) ----------
interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}

export function Field({ label, htmlFor, error, hint, children }: FieldProps) {
  return (
    <div>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ---------- Card ----------
export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl bg-white border border-slate-200 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

export function CardHeader({ children }: { children: ReactNode }) {
  return <div className="px-6 py-4 border-b border-slate-100">{children}</div>;
}

export function CardBody({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`px-6 py-4 ${className}`}>{children}</div>;
}

// ---------- Alert ----------
export function Alert({
  variant = "error",
  children,
}: {
  variant?: "error" | "info" | "success";
  children: ReactNode;
}) {
  const styles = {
    error: "bg-red-50 border-red-200 text-red-800",
    info: "bg-blue-50 border-blue-200 text-blue-800",
    success: "bg-emerald-50 border-emerald-200 text-emerald-800",
  }[variant];
  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${styles}`}>
      {children}
    </div>
  );
}

// ---------- Badge ----------
export function Badge({
  children,
  variant = "neutral",
}: {
  children: ReactNode;
  variant?: "neutral" | "success" | "warning" | "danger";
}) {
  const styles = {
    neutral: "bg-slate-100 text-slate-700",
    success: "bg-emerald-100 text-emerald-700",
    warning: "bg-amber-100 text-amber-700",
    danger: "bg-red-100 text-red-700",
  }[variant];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles}`}>
      {children}
    </span>
  );
}

// ---------- Dialog (modal simple, sin librería) ----------
interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg";
}

export function Dialog({ open, onClose, title, description, children, size = "md" }: DialogProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const maxWidth = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" }[size];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative bg-white rounded-2xl shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-hidden flex flex-col`}
      >
        <header className="flex items-start justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            {description && (
              <p className="text-xs text-slate-500 mt-1">{description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded p-1 -mt-1"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-6 py-4 overflow-auto">{children}</div>
      </div>
    </div>
  );
}

// ---------- ConfirmDialog ----------
/**
 * Confirmación para lo que no se deshace: cancelar una cita, quitar a un
 * apoderado, anular algo.
 *
 * Existe porque un clic de más en una papelera no puede costar el contacto de
 * un menor ni la hora de un paciente. La regla del proyecto es simple: si la
 * acción quita, cancela o anula algo, pasa por aquí.
 *
 * El texto de `confirmLabel` dice QUÉ va a pasar ("Sí, cancelar la cita"), no
 * "Aceptar": quien lee un botón a medias tiene que entender igual qué eligió.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  detalle,
  confirmLabel = "Sí, continuar",
  cancelLabel = "No, volver",
  variant = "danger",
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  /** Qué exactamente se va a tocar: nombre, fecha, importe. */
  detalle?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "primary";
  loading?: boolean;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={title} size="sm">
      <div className="space-y-4">
        {description && <p className="text-sm text-slate-600">{description}</p>}
        {detalle && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
            {detalle}
          </div>
        )}
        <div className="flex justify-end gap-2">
          {/* Volver es lo seguro y por eso va primero y sin peso visual. */}
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={variant} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
